import { useState, useEffect, useMemo } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useTenantId } from '@/hooks/useTenantId';
import { toast } from '@/components/ui/use-toast';
import { generateRecurringInstances } from '@/lib/recurrenceUtils';
import { startOfMonth, endOfMonth, addMonths } from 'date-fns';

// @ts-ignore - Type instantiation issues with events table
const db: any = supabase;

export interface Event {
  id: string;
  user_id: string;
  title: string;
  description?: string;
  start_time: string;
  end_time: string;
  event_type: string;
  location?: string;
  lead_id?: string;
  project_id?: string;
  created_at: string;
  updated_at: string;
  is_recurring?: boolean;
  recurrence_rule?: string;
  recurrence_end_date?: string;
  parent_event_id?: string;
}

export interface EventFormData {
  title: string;
  description?: string;
  start_time: string;
  end_time: string;
  event_type: string;
  location?: string;
  lead_id?: string;
  project_id?: string;
  is_recurring?: boolean;
  recurrence_rule?: string;
  recurrence_end_date?: string;
}

export function useEvents() {
  const [events, setEvents] = useState<Event[]>([]);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();
  const { tenantId } = useTenantId();

  const fetchEvents = async () => {
    if (!user || !tenantId) return;

    setLoading(true);
    try {
      const { data, error } = await db
        .from('events')
        .select('*')
        .eq('user_id', user.id)
        .eq('tenant_id', tenantId)
        .order('start_time', { ascending: true });

      if (error) throw error;
      setEvents(data || []);
    } catch (error) {
      console.error('Error fetching events:', error);
      toast({
        title: "Error",
        description: "Failed to fetch events. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const createEvent = async (eventData: EventFormData) => {
    if (!user) return { error: 'User not authenticated' };
    if (!tenantId) return { error: 'Tenant not found' };

    try {
      const { data, error } = await db
        .from('events')
        .insert([{
          ...eventData,
          user_id: user.id,
          tenant_id: tenantId,
        }])
        .select()
        .single();

      if (error) throw error;

      setEvents(prev => [...prev, data]);
      toast({
        title: "Success",
        description: "Event created successfully.",
      });

      return { data, error: null };
    } catch (error) {
      console.error('Error creating event:', error);
      toast({
        title: "Error",
        description: "Failed to create event. Please try again.",
        variant: "destructive",
      });
      return { error };
    }
  };

  const updateEvent = async (id: string, eventData: Partial<EventFormData>) => {
    if (!user) return { error: 'User not authenticated' };

    try {
      const { data, error } = await db
        .from('events')
        .update(eventData)
        .eq('id', id)
        .eq('user_id', user.id)
        .select()
        .single();

      if (error) throw error;

      setEvents(prev => prev.map(event => event.id === id ? data : event));
      toast({
        title: "Success",
        description: "Event updated successfully.",
      });

      return { data, error: null };
    } catch (error) {
      console.error('Error updating event:', error);
      toast({
        title: "Error",
        description: "Failed to update event. Please try again.",
        variant: "destructive",
      });
      return { error };
    }
  };

  const deleteEvent = async (id: string) => {
    if (!user) return { error: 'User not authenticated' };

    try {
      const { error } = await db
        .from('events')
        .delete()
        .eq('id', id)
        .eq('user_id', user.id);

      if (error) throw error;

      setEvents(prev => prev.filter(event => event.id !== id));
      toast({
        title: "Success",
        description: "Event deleted successfully.",
      });

      return { error: null };
    } catch (error) {
      console.error('Error deleting event:', error);
      toast({
        title: "Error",
        description: "Failed to delete event. Please try again.",
        variant: "destructive",
      });
      return { error };
    }
  };

  useEffect(() => {
    fetchEvents();
  }, [user, tenantId]);

  // Expand recurring events for the current view
  const expandedEvents = useMemo(() => {
    const now = new Date();
    const viewStart = startOfMonth(addMonths(now, -1));
    const viewEnd = endOfMonth(addMonths(now, 2));

    return events.flatMap(event => 
      generateRecurringInstances(event, viewStart, viewEnd)
    );
  }, [events]);

  return {
    events: expandedEvents,
    loading,
    createEvent,
    updateEvent,
    deleteEvent,
    refetch: fetchEvents,
  };
}